import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';
import { Capacitor, PermissionState } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { ClientJS } from 'clientjs';
import { AppConstants } from '../commons/app-constants';
import { APP_VERSION } from 'src/environments/version';
import { environment } from 'src/environments/environment';

export interface ForegroundPushEvent {
  title: string;
  body: string;
  data: Record<string, string>;
}

// FCM push notification registration — native Android (Capacitor) AND web browser. init()
// branches on Capacitor.isNativePlatform(): native sets up the Capacitor plugin/permission flow;
// web calls initWeb() (Firebase JS SDK + a service worker). Both funnel into the same
// registerPendingTokenIfNeeded()/unregisterCurrentToken() pair, called once from AppComponent's
// constructor (same pattern as DeepLinkService); the actual backend registration call is deferred
// to registerPendingTokenIfNeeded(), called from AppComponent.loadBootstrapData() on every
// authenticated navigation, since registration requires a live session and the token can arrive
// before login on cold start. Backend treats platform as inert metadata — every lifecycle
// behavior (logout, forced-logout, marketing opt-in/broadcast) already works identically for both.
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);

  private pendingToken: string | null = null;
  private lastRegisteredToken: string | null = null;
  private registrationResolvers: Array<{ resolve: () => void; reject: (err: any) => void }> = [];

  // Last-known OS/browser permission state, set by init()'s silent checkPermissions() call and
  // kept current by requestPermissionNow(). UI (the onboarding priming dialog, the Account
  // Details recovery row) reads this to tell "never asked" (prompt/prompt-with-rationale) apart
  // from "denied" without triggering a real native call just to find out.
  lastKnownPermissionState: PermissionState | 'default' | 'unsupported' = 'default';

  // Emits when a push arrives while the app is in the foreground (native or web) — consumed by
  // AppComponent to show an in-app toast. Deliberately NOT done by injecting MessageService
  // directly into this service: MessageService is re-provided at the AppComponent level
  // (app.component.ts's own `providers` array), so a root-provided singleton like this service
  // would otherwise resolve a DIFFERENT MessageService instance than the one AppComponent's
  // <p-toast> outlets are actually listening to, and .add() would silently show nothing. Routing
  // the event through this Subject lets AppComponent call its own, correctly-scoped
  // MessageService itself.
  private readonly foregroundPush$ = new Subject<ForegroundPushEvent>();
  readonly foregroundPush = this.foregroundPush$.asObservable();

  // Android 8+ (API 26) plays a notification's custom sound ONLY if the NotificationChannel it is
  // posted to was created with that sound — the FCM payload's AndroidNotification.setSound() is
  // silently ignored on API 26+. The backend sets .setChannelId(this exact id) on every push, so
  // this channel must exist on-device, created with the bundled raw sound
  // (android/app/src/main/res/raw/no_problem_notification_sound.mp3). Channel settings are
  // immutable once created, so the id is versioned — bump the suffix if the sound ever changes.
  private readonly pushChannelId = 'lumberlinq_alerts_v1';

  init(): void {
    if (!Capacitor.isNativePlatform()) {
      this.initWeb();
      return;
    }

    void this.createAndroidNotificationChannel();

    PushNotifications.addListener('registration', (token: Token) => {
      this.ngZone.run(() => {
        this.pendingToken = token.value;
        this.registerPendingTokenIfNeeded();
        this.registrationResolvers.forEach((r) => r.resolve());
        this.registrationResolvers = [];
      });
    });

    PushNotifications.addListener('registrationError', (err: any) => {
      // .error() — deliberately not .warn()/.debug(): environment.prod.ts sets logLevel
      // 'ERROR', which silently drops anything below that. This is the one signal that
      // must survive in prod if FCM registration fails on-device.
      this.logger.error('[PushNotification] Registration error', err);
      this.registrationResolvers.forEach((r) => r.reject(err));
      this.registrationResolvers = [];
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.ngZone.run(() => {
        this.logger.debug('[PushNotification] Received in foreground', notification);
        this.foregroundPush$.next({
          title: notification.title ?? 'LumberLinq',
          body: notification.body ?? '',
          data: notification.data ?? {},
        });
      });
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.logger.debug('[PushNotification] Tapped', action);
    });

    // Deliberately does NOT auto-call requestPermissions() when not yet granted — the native OS
    // prompt is now only ever triggered by an explicit user action via requestPermissionNow()
    // (see the onboarding priming dialog / Account Details recovery row), never automatically on
    // cold start. Silently checking here just keeps lastKnownPermissionState current for that UI.
    this.callWithRetry(() => PushNotifications.checkPermissions(), 'checkPermissions').then((res) => {
      this.lastKnownPermissionState = res.receive;
      if (res.receive === 'granted') {
        this.registerWithRetry();
      }
    }).catch((err) => {
      this.logger.error('[PushNotification] checkPermissions() failed', err);
    });
  }

  // True when there's a real reason to show the "Enable notifications?" priming dialog: permission
  // hasn't been granted yet and the platform actually supports asking (native always does; web
  // only if the Notification API exists at all). Safe to call even before init()'s own
  // checkPermissions() has resolved — lastKnownPermissionState starts at 'default', which this
  // treats the same as "not yet granted," so worst case the dialog shows once redundantly; tapping
  // "Enable" against an already-granted permission just resolves instantly with no OS popup.
  shouldPrimeForPermission(): boolean {
    return this.lastKnownPermissionState !== 'granted' && this.lastKnownPermissionState !== 'unsupported';
  }

  // Stricter than shouldPrimeForPermission(): true only when the OS/browser can still actually
  // show its prompt — i.e. the user has NEVER been asked. 'denied' returns false here (the OS
  // will silently no-op a re-request; only the system settings screen can change it), so the
  // app-wide auto-prompt on login doesn't nag users who already said no. 'granted'/'unsupported'
  // are likewise excluded.
  isPermissionPromptable(): boolean {
    return this.lastKnownPermissionState === 'prompt'
      || this.lastKnownPermissionState === 'prompt-with-rationale'
      || this.lastKnownPermissionState === 'default';
  }

  // Android-only. Creates the notification channel the backend targets (.setChannelId) so its
  // bundled custom sound actually plays on API 26+ — see pushChannelId's comment. Idempotent:
  // createChannel on an existing id is a no-op (and can't change its settings anyway). Runs
  // regardless of permission state — channel creation needs no permission.
  private async createAndroidNotificationChannel(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;
    try {
      await PushNotifications.createChannel({
        id: this.pushChannelId,
        name: 'LumberLinq alerts',
        description: 'Shipment, tally, inventory and account notifications',
        sound: 'no_problem_notification_sound',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
    } catch (err) {
      this.logger.error('[PushNotification] createChannel failed', err);
    }
  }

  // The one place that actually triggers the OS/browser permission prompt — called explicitly
  // from a real user action (the onboarding "Enable notifications?" dialog, or the Account
  // Details recovery row), never automatically. Resolves with the resulting permission state so
  // callers can update their own UI immediately rather than re-reading lastKnownPermissionState
  // after a delay.
  async requestPermissionNow(): Promise<PermissionState | 'default' | 'unsupported'> {
    if (!Capacitor.isNativePlatform()) {
      return this.requestWebPermissionNow();
    }
    try {
      const req = await this.callWithRetry(() => PushNotifications.requestPermissions(), 'requestPermissions');
      this.lastKnownPermissionState = req.receive;
      if (req.receive === 'granted') this.registerWithRetry();
      return req.receive;
    } catch (err) {
      this.logger.error('[PushNotification] requestPermissionNow() failed', err);
      return this.lastKnownPermissionState;
    }
  }

  // Cached across calls so requestWebPermissionNow() (triggered later by an explicit user action)
  // doesn't need to re-register the service worker / re-init the Firebase app from scratch.
  private webMessaging: Messaging | null = null;
  private webRegistration: ServiceWorkerRegistration | null = null;

  // Browser (non-native) push setup. A denied/not-yet-granted permission or an unsupported
  // browser is a silent, expected no-op (never surfaced to the user) — but a genuine failure (bad
  // SW registration, invalid Firebase config, getToken() rejecting) is logged via .error(), same
  // as the native path's registrationError handler, and for the same reason: environment.prod.ts
  // sets logLevel 'ERROR', which silently drops anything below that, so .debug()/.warn() here
  // would be permanently invisible in prod — .error() is what must survive to be debuggable at
  // all. Deliberately does NOT auto-call Notification.requestPermission() — the browser prompt is
  // now only ever triggered by an explicit user action via requestWebPermissionNow(). This method
  // still sets up the service worker/foreground-message listener and fetches a token immediately
  // if permission was already granted in a past session. Foreground messages go through the same
  // foregroundPush$ toast path as native (see 'pushNotificationReceived' above) rather than a
  // native OS Notification() popup — a foregrounded tab already has the user's attention, an OS
  // popup on top of an in-page toast would be redundant. Background/closed-tab messages are
  // unaffected — those are still handled entirely by the service worker's own onBackgroundMessage,
  // which shows the native OS notification exactly as before.
  private async initWeb(): Promise<void> {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      this.logger.debug('[PushNotification] Browser lacks Notification/ServiceWorker support — skipping web push');
      this.lastKnownPermissionState = 'unsupported';
      return;
    }

    this.lastKnownPermissionState = Notification.permission;

    try {
      this.webRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const app = initializeApp(environment.firebaseWebConfig);
      this.webMessaging = getMessaging(app);

      onMessage(this.webMessaging, (payload) => {
        this.ngZone.run(() => {
          this.foregroundPush$.next({
            title: payload.notification?.title ?? 'LumberLinq',
            body: payload.notification?.body ?? '',
            data: (payload.data as Record<string, string>) ?? {},
          });
        });
      });

      if (Notification.permission === 'granted') {
        await this.fetchAndRegisterWebToken();
      }
    } catch (err) {
      // The one signal that must survive prod logging if web push setup fails — see comment above.
      this.logger.error('[PushNotification] Web push setup failed', err);
    }
  }

  // The one place that actually triggers the browser's permission prompt — called explicitly from
  // requestPermissionNow() on the web platform, never automatically. A 'denied' result here cannot
  // be reset programmatically (browser restriction) — the caller's UI must point the user at their
  // own browser's site-settings instead of retrying this.
  private async requestWebPermissionNow(): Promise<PermissionState | 'default' | 'unsupported'> {
    if (!('Notification' in window)) return 'unsupported';
    try {
      const permission = await Notification.requestPermission();
      this.lastKnownPermissionState = permission;
      if (permission === 'granted') await this.fetchAndRegisterWebToken();
      return permission;
    } catch (err) {
      this.logger.error('[PushNotification] requestWebPermissionNow() failed', err);
      return this.lastKnownPermissionState;
    }
  }

  private async fetchAndRegisterWebToken(): Promise<void> {
    if (!this.webMessaging || !this.webRegistration) {
      this.logger.error('[PushNotification] fetchAndRegisterWebToken() called before web push setup completed');
      return;
    }
    try {
      const token = await getToken(this.webMessaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: this.webRegistration,
      });
      if (!token) {
        this.logger.error('[PushNotification] getToken() returned no token despite granted permission');
        return;
      }
      this.ngZone.run(() => {
        this.pendingToken = token;
        this.registerPendingTokenIfNeeded();
      });
    } catch (err) {
      this.logger.error('[PushNotification] fetchAndRegisterWebToken() failed', err);
    }
  }

  // @capacitor/push-notifications' Android bridge has a confirmed bug (observed directly via
  // Chrome DevTools Protocol against a live device): the FIRST-ever call to a plugin method
  // after fresh app launch never gets its native response delivered — the promise just hangs
  // forever, no resolve, no reject. A second identical call on the same session resolves in
  // ~10-20ms. Racing against a timeout and retrying works around it without needing to
  // understand the plugin's internal cause.
  private callWithRetry<T extends { receive: string }>(
    fn: () => Promise<T>,
    label: string,
    timeoutMs = 4000,
    maxAttempts = 3,
  ): Promise<T> {
    const attempt = (n: number): Promise<T> => new Promise<T>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        if (n < maxAttempts) {
          this.logger.error(`[PushNotification] ${label} timed out (attempt ${n}/${maxAttempts}) — retrying`);
          resolve(attempt(n + 1));
        } else {
          reject(new Error(`${label} timed out after ${maxAttempts} attempts`));
        }
      }, timeoutMs);
      fn().then((r) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(r);
      }).catch((e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(e);
      });
    });
    return attempt(1);
  }

  // PushNotifications.register() itself has no return value to await — it only ever resolves
  // via the 'registration'/'registrationError' listeners firing asynchronously, sometime later.
  // Unlike checkPermissions/requestPermissions (wrapped in callWithRetry above), register() had
  // no timeout/retry protection at all — so if this is the same class of "first native plugin
  // call after fresh launch never gets a response" bug already confirmed on Android (see
  // callWithRetry's comment), a hung register() call would silently never resolve, with nothing
  // to retry it. This mirrors that same retry pattern for register() specifically.
  private registerWithRetry(maxAttempts = 3, timeoutMs = 6000): void {
    const attempt = (n: number): void => {
      PushNotifications.register();
      const settled = new Promise<void>((resolve, reject) => {
        this.registrationResolvers.push({ resolve, reject });
      });
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('register-timeout')), timeoutMs);
      });
      Promise.race([settled, timeout]).catch((err) => {
        if (err?.message === 'register-timeout' && n < maxAttempts) {
          attempt(n + 1);
        }
        // A genuine registrationError already logged itself in the listener above; exhausting
        // all retries on a persistent timeout is likewise already covered by that same path
        // once/if a late response ever arrives — nothing further to do here.
      });
    };
    attempt(1);
  }

  // Idempotent — safe to call on every navigation. Only actually hits the backend when a new,
  // not-yet-registered token is pending (i.e. at most once per token per session). Deliberately
  // does NOT gate on localStorage[AppConstants.USER] — that flag is only set at fresh
  // interactive login and isn't reliably rehydrated on a resumed session with a still-valid
  // HttpOnly auth cookie (confirmed directly: a real logged-in session with visible
  // authenticated data still read NOT_LOGGED_IN from it, silently blocking every registration
  // attempt). The backend's own @AuthenticationPrincipal requirement is the real guard — a
  // genuinely unauthenticated call just 401s harmlessly here.
  registerPendingTokenIfNeeded(): void {
    if (!this.pendingToken || this.pendingToken === this.lastRegisteredToken) return;

    const token = this.pendingToken;
    const deviceId = new ClientJS().getFingerprint()?.toString() ?? '';
    this.http.post(`${AppConstants.API_URL}api/v1/push/devices`, {
      token,
      platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform().toUpperCase() : 'WEB',
      deviceId,
      appVersion: APP_VERSION,
    }, { withCredentials: true }).subscribe({
      next: () => { this.lastRegisteredToken = token; },
      error: (err) => this.logger.error('[PushNotification] Failed to register device token', err),
    });
  }

  // Called at the exact moment a login completes (see AppComponent's router subscription) —
  // deliberately bypasses the lastRegisteredToken guard below rather than relying on it. That
  // guard only tracks whether THIS tab already sent this token value; it has no idea whether the
  // server-side row was since deactivated by some other path (session expiry, FCM's own
  // UNREGISTERED callback, device reassignment). Without this, a same-tab logout/session-expiry
  // → re-login cycle could leave the user logged in with a push token the backend still thinks
  // is inactive, since registerPendingTokenIfNeeded() would see "unchanged" and skip the call.
  forceReregisterOnLogin(): void {
    this.lastRegisteredToken = null;
    this.registerPendingTokenIfNeeded();
  }

  unregisterCurrentToken(): Promise<void> {
    if (!this.lastRegisteredToken) return Promise.resolve();
    const token = this.lastRegisteredToken;
    this.lastRegisteredToken = null;
    return new Promise((resolve) => {
      this.http.post(`${AppConstants.API_URL}api/v1/push/devices/unregister`, { token }, { withCredentials: true })
        .subscribe({ next: () => resolve(), error: () => resolve() });
    });
  }
}
